import {
  DESIGN_STORAGE_KEY,
  type DesignConfig,
} from '@/types/design'

export function readCachedDesign(): DesignConfig | null {
  try {
    const raw = localStorage.getItem(DESIGN_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as DesignConfig
    if (!parsed?.logos?.dark_logo || !parsed?.pwa?.title) return null
    return parsed
  } catch {
    return null
  }
}

export function writeCachedDesign(config: DesignConfig): void {
  try {
    localStorage.setItem(DESIGN_STORAGE_KEY, JSON.stringify(config))
  } catch {
    /* private mode / quota */
  }
}

/**
 * Apply PWA / favicon / document title from design payload.
 * Safe to call repeatedly (idempotent DOM updates).
 */
export function applyDesignToDocument(config: DesignConfig): void {
  document.title = config.pwa.title

  const setMeta = (selector: string, attr: string, value: string) => {
    let el = document.querySelector(selector)
    if (!el) {
      if (selector.startsWith('meta[')) {
        el = document.createElement('meta')
        const match = /meta\[([^=]+)="([^"]+)"\]/.exec(selector)
        if (match) el.setAttribute(match[1], match[2])
        document.head.appendChild(el)
      } else if (selector.startsWith('link[')) {
        el = document.createElement('link')
        const match = /link\[([^=]+)="([^"]+)"\]/.exec(selector)
        if (match) el.setAttribute(match[1], match[2])
        document.head.appendChild(el)
      }
    }
    el?.setAttribute(attr, value)
  }

  setMeta('meta[name="description"]', 'content', config.pwa.description)
  setMeta('meta[name="application-name"]', 'content', config.pwa.title)
  setMeta('meta[name="apple-mobile-web-app-title"]', 'content', config.pwa.title)

  const favicon = config.favicon_url || config.pwa.icon_url
  setMeta('link[rel="icon"]', 'href', favicon)
  setMeta('link[rel="apple-touch-icon"]', 'href', config.pwa.icon_url)

  // Dynamic web app manifest for Add-to-Home-Screen
  const manifest = {
    name: config.pwa.title,
    short_name: config.pwa.title.slice(0, 12),
    description: config.pwa.description,
    start_url: '/',
    scope: '/',
    display: 'standalone',
    background_color: '#0b0e11',
    theme_color: '#0b0e11',
    icons: [
      {
        src: config.pwa.icon_url,
        sizes: '192x192',
        type: 'image/png',
        purpose: 'any maskable',
      },
      {
        src: config.pwa.icon_url,
        sizes: '512x512',
        type: 'image/png',
        purpose: 'any maskable',
      },
    ],
  }

  const blob = new Blob([JSON.stringify(manifest)], {
    type: 'application/manifest+json',
  })
  const url = URL.createObjectURL(blob)
  let link = document.querySelector<HTMLLinkElement>('link[rel="manifest"]')
  if (!link) {
    link = document.createElement('link')
    link.rel = 'manifest'
    document.head.appendChild(link)
  } else if (link.href.startsWith('blob:')) {
    URL.revokeObjectURL(link.href)
  }
  link.href = url
}
